import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import { journalInvoiceRaised, journalCustomerPayment } from '../lib/journalEngine'

const PAYMENT_TERMS = [0,7,14,30,60,90,180]
const STATUS_COLORS = { Unpaid:'badge-amber', Partial:'badge-blue', Paid:'badge-green', Overdue:'badge-red', Disputed:'badge-grey' }

export default function Invoices() {
  const { user, profile, canEditData } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [customers, setCustomers] = useState([])
  const [tab, setTab] = useState('list')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [statusFilter, setStatusFilter] = useState('All')
  const [paymentModal, setPaymentModal] = useState(null)
  const [payForm, setPayForm] = useState({ amount:'', payment_method:'Bank Transfer', reference:'', notes:'' })
  const [creditWarning, setCreditWarning] = useState(null)
  const [creditOverride, setCreditOverride] = useState(false)
  const [creditOverrideReason, setCreditOverrideReason] = useState('')

  const emptyItem = () => ({ description:'', qty:'', unit_price:'', total:'' })
  const [form, setForm] = useState({
    customer_id:'', invoice_number:'',
    invoice_date: ugandaDate(),
    payment_terms:30, due_date:'', customer_name:'',
    customer_email:'', notes:'', items:[emptyItem()]
  })

  useEffect(() => {
    loadInvoices()
    supabase.from('customer_balances').select('*').order('name').then(({ data }) => setCustomers(data || []))
  }, [])

  const loadInvoices = async () => {
    setLoading(true)
    const { data } = await supabase.from('invoices')
      .select('*, profiles(full_name), invoice_payments(*)')
      .order('invoice_date', { ascending:false })
    setInvoices(data || [])
    setLoading(false)
  }

  const handleCustomerSelect = (customerId) => {
    const c = customers.find(c => c.id === customerId)
    if (!c) { setForm(f => ({ ...f, customer_id:'', customer_name:'', customer_email:'' })); setCreditWarning(null); return }
    setForm(f => ({
      ...f, customer_id: customerId,
      customer_name: c.name, customer_email: c.email || '',
      payment_terms: c.payment_terms || 30
    }))
    // Check credit and overdue status
    const warnings = []
    if (c.over_credit_limit) warnings.push(`⚠️ ${c.name} is OVER their credit limit of UGX ${c.credit_limit.toLocaleString()}. Outstanding balance: UGX ${c.outstanding_balance.toLocaleString()}.`)
    if (c.days_oldest_unpaid > 14) warnings.push(`⚠️ ${c.name} has invoices overdue by ${c.days_oldest_unpaid - (c.payment_terms||0)} days. Both Edward and Sharon have been notified.`)
    if (c.is_on_hold) warnings.push(`🚫 ${c.name} is ON HOLD: ${c.hold_reason || 'Contact management.'}`)
    setCreditWarning(warnings.length > 0 ? warnings : null)
    setCreditOverride(false); setCreditOverrideReason('')
  }

  const setItem = (i, field, val) => {
    const items = [...form.items]
    items[i] = { ...items[i], [field]: val }
    if (field === 'qty' || field === 'unit_price') {
      const qty = parseFloat(field==='qty'?val:items[i].qty)||0
      const price = parseFloat(field==='unit_price'?val:items[i].unit_price)||0
      items[i].total = (qty*price).toFixed(0)
    }
    setForm(f => ({ ...f, items }))
  }

  const totalAmount = form.items.reduce((s,r) => s + (parseFloat(r.total)||0), 0)

  const handleSave = async () => {
    if (!form.customer_name) { setMsg({ type:'error', text:'Customer name is required.' }); return }
    if (!form.invoice_number) { setMsg({ type:'error', text:'Invoice number is required.' }); return }
    if (creditWarning && !creditOverride) { setMsg({ type:'error', text:'You must acknowledge the credit warning before saving.' }); return }
    if (creditOverride && creditOverrideReason.length < 20) { setMsg({ type:'error', text:'Please provide a detailed reason (at least 20 characters) for the credit override.' }); return }

    // Calculate due date
    const invoiceDate = new Date(form.invoice_date)
    const dueDate = new Date(invoiceDate)
    dueDate.setDate(dueDate.getDate() + parseInt(form.payment_terms||0))

    setSaving(true); setMsg(null)
    const { error } = await supabase.from('invoices').insert({
      ...form, user_id:user.id,
      amount: totalAmount, due_amount: totalAmount,
      balance_due: totalAmount, amount_paid:0,
      due_date: ugandaDate(dueDate),
      items: form.items.filter(r => r.description),
      credit_override: creditOverride,
      credit_override_reason: creditOverrideReason || null,
      collection_status: 'current',
    })
    if (error) { setMsg({ type:'error', text:error.message }); setSaving(false); return }
    // Dr 1201 Trade Receivables / Cr income account (from crop cycle, or fallback to 4105)
    const { data: savedInv } = await supabase.from('invoices').select('id').eq('invoice_number', form.invoice_number).single()
    if (savedInv) {
      // Look up income_account from the crop cycle if linked
      let revenueAcc = '4105' // default: Other Crop Sales
      if (form.crop_cycle_id) {
        const { data: cycle } = await supabase
          .from('crop_cycles').select('income_account,crop,variety').eq('id', form.crop_cycle_id).single()
        if (cycle?.income_account) revenueAcc = cycle.income_account
      } else {
        // Fallback: infer from item descriptions for existing invoices
        revenueAcc = form.items?.some(i => i.description?.toLowerCase().includes('lettuce') && i.description?.toLowerCase().includes('grade a')) ? '4101'
          : form.items?.some(i => i.description?.toLowerCase().includes('lettuce')) ? '4102'
          : form.items?.some(i => i.description?.toLowerCase().includes('strawberry')) ? '4103'
          : '4105'
      }
      await journalInvoiceRaised({
        invoiceId: savedInv.id, invoiceNumber: form.invoice_number,
        customer: form.customer_name, amount: totalAmount,
        revenueAccount: revenueAcc,
        date: form.invoice_date, userId: user.id,
      })
    }
    setMsg({ type:'success', text:'Invoice saved!' })
    setSaving(false); setTab('list'); loadInvoices()
    setForm({ customer_id:'', invoice_number:'', invoice_date:ugandaDate(), payment_terms:30, due_date:'', customer_name:'', customer_email:'', notes:'', items:[emptyItem()] })
    setCreditWarning(null); setCreditOverride(false); setCreditOverrideReason('')
    setTimeout(() => setMsg(null), 3000)
  }

  const handleRecordPayment = async () => {
    if (!payForm.amount) return
    setSaving(true)
    const { error } = await supabase.from('invoice_payments').insert({
      invoice_id: paymentModal.id,
      received_by: user.id,
      amount: parseFloat(payForm.amount),
      payment_date: ugandaDate(),
      payment_method: payForm.payment_method,
      reference: payForm.reference,
      notes: payForm.notes,
    })
    if (!error) {
      // Dr 1101 Cash / Cr 1201 Trade Receivables
      const { data: savedPayment } = await supabase.from('invoice_payments')
        .select('id').eq('invoice_id', paymentModal.id).order('created_at', { ascending: false }).limit(1).single()
      if (savedPayment) {
        await journalCustomerPayment({
          paymentId: savedPayment.id,
          invoiceId: paymentModal.id,
          customer: paymentModal.customer_name,
          amount: parseFloat(payForm.amount),
          date: ugandaDate(),
          userId: user.id,
        })
      }
      setMsg({ type:'success', text:`Payment of UGX ${parseInt(payForm.amount).toLocaleString()} recorded.` })
      setPaymentModal(null)
      setPayForm({ amount:'', payment_method:'Bank Transfer', reference:'', notes:'' })
      loadInvoices()
    }
    setSaving(false)
    setTimeout(() => setMsg(null), 3000)
  }

  const nextInvoiceNum = () => {
    const nums = invoices.map(i => parseInt(i.invoice_number?.replace(/\D/g,''))).filter(Boolean)
    const next = nums.length ? Math.max(...nums)+1 : 1
    return `INV-${String(next).padStart(4,'0')}`
  }

  const handlePrintInvoice = (inv) => {
    const w = window.open('','_blank')
    const items = inv.items || []
    const dueDate = inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-UG') : '—'
    w.document.write(`<html><head><title>Invoice ${inv.invoice_number}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:40px;color:#1C3520;}
      h1{font-size:1.6rem;margin-bottom:4px;}
      .sub{color:#666;font-size:0.85rem;margin-bottom:28px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px;}
      .label{font-size:0.7rem;text-transform:uppercase;letter-spacing:0.1em;color:#888;margin-bottom:3px;}
      .val{font-weight:600;font-size:0.95rem;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1C3520;color:white;padding:10px 14px;text-align:left;font-size:0.82rem;}
      td{padding:10px 14px;border-bottom:1px solid #eee;font-size:0.85rem;}
      .total-row{font-weight:700;font-size:1rem;text-align:right;padding:12px 14px;border-top:2px solid #1C3520;}
      .status{display:inline-block;padding:4px 12px;border-radius:20px;font-size:0.75rem;font-weight:700;text-transform:uppercase;}
      .unpaid{background:#fef3cd;color:#856404;} .paid{background:#d1fae5;color:#065f46;} .partial{background:#dbeafe;color:#1e3a5f;}
      .footer{margin-top:40px;padding-top:20px;border-top:1px solid #eee;font-size:0.75rem;color:#888;text-align:center;}
      @media print{body{padding:20px;}}
    </style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;">
      <div>
        <h1>INVOICE</h1>
        <div class="sub">Kobflo Greentek Ltd · app.kobfloagri.com</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:1.4rem;font-weight:700;font-family:Georgia,serif;">${inv.invoice_number}</div>
        <span class="status ${inv.status==='Paid'?'paid':inv.status==='Partial'?'partial':'unpaid'}">${inv.status}</span>
      </div>
    </div>
    <div class="grid">
      <div>
        <div class="label">Bill To</div>
        <div class="val">${inv.customer_name}</div>
        ${inv.customer_email?`<div style="font-size:0.82rem;color:#666;margin-top:2px;">${inv.customer_email}</div>`:''}
      </div>
      <div>
        <div class="grid" style="gap:12px;">
          <div><div class="label">Invoice Date</div><div class="val">${new Date(inv.invoice_date).toLocaleDateString('en-UG')}</div></div>
          <div><div class="label">Due Date</div><div class="val" style="color:${inv.status==='Overdue'?'#8B2E2E':'inherit'}">${dueDate}</div></div>
          <div><div class="label">Payment Terms</div><div class="val">${inv.payment_terms===0?'On Delivery':`Net ${inv.payment_terms} days`}</div></div>
        </div>
      </div>
    </div>
    <table>
      <thead><tr><th>Description</th><th style="text-align:center;">Qty (kg)</th><th style="text-align:right;">Unit Price (UGX)</th><th style="text-align:right;">Total (UGX)</th></tr></thead>
      <tbody>
        ${items.map(it=>`<tr><td>${it.description||'—'}</td><td style="text-align:center;">${it.qty||'—'}</td><td style="text-align:right;">${parseFloat(it.unit_price||0).toLocaleString()}</td><td style="text-align:right;">${parseFloat(it.total||0).toLocaleString()}</td></tr>`).join('')}
      </tbody>
      <tfoot>
        <tr><td colspan="3" class="total-row">TOTAL</td><td class="total-row">UGX ${(inv.amount||0).toLocaleString()}</td></tr>
        ${inv.amount_paid>0?`<tr><td colspan="3" class="total-row" style="color:#065f46;">PAID</td><td class="total-row" style="color:#065f46;">UGX ${(inv.amount_paid||0).toLocaleString()}</td></tr>`:''}
        ${(inv.balance_due||0)>0?`<tr><td colspan="3" class="total-row" style="color:#856404;">BALANCE DUE</td><td class="total-row" style="color:#856404;">UGX ${(inv.balance_due||0).toLocaleString()}</td></tr>`:''}
      </tfoot>
    </table>
    ${inv.notes?`<div style="background:#f9f5ec;border-radius:6px;padding:14px;font-size:0.82rem;margin-bottom:20px;"><strong>Notes:</strong> ${inv.notes}</div>`:''}
    <div class="footer">
      Thank you for your business · Kobflo Greentek Ltd<br/>
      Payment due ${dueDate}. Late payments may attract charges. Queries within 48 hours of invoice date.
    </div>
    </body></html>`)
    w.document.close(); w.print()
  }

  const filtered = statusFilter==='All' ? invoices : invoices.filter(i => i.status===statusFilter)
  const totalUnpaid = invoices.filter(i => ['Unpaid','Partial','Overdue'].includes(i.status)).reduce((s,i) => s+(i.balance_due||0),0)

  return (
    <AppLayout title="Invoices">
      <div className="section-header">
        <div className="section-icon">📄</div>
        <div className="section-info">
          <h2>Invoices</h2>
          <p>Create and track invoices. Supports partial payments and customer credit limits.</p>
        </div>
        {canEditData && <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => { setForm(f=>({...f,invoice_number:nextInvoiceNum()})); setTab('new') }}>+ New Invoice</button>}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18 }}>
        {[
          { label:'Total Invoices', val:invoices.length },
          { label:'Unpaid', val:invoices.filter(i=>i.status==='Unpaid').length, color:'amber' },
          { label:'Overdue', val:invoices.filter(i=>i.status==='Overdue').length, color:'red' },
          { label:'Outstanding (UGX)', val:totalUnpaid.toLocaleString(), color:invoices.filter(i=>i.status==='Overdue').length>0?'red':'' },
        ].map(k => (
          <div key={k.label} className={`kpi ${k.color||''}`}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value" style={{ fontSize:'1.4rem' }}>{k.val}</div>
          </div>
        ))}
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='list'?'active':''}`} onClick={() => setTab('list')}>📋 All Invoices</button>
        {canEditData && <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>✏️ New Invoice</button>}
        <button className={`tab ${tab==='advances'?'active':''}`} onClick={() => setTab('advances')}>💰 Advance Payments</button>
      </div>

      {tab === 'list' && (
        <div className="card">
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            {['All','Unpaid','Partial','Paid','Overdue','Disputed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={{ padding:'5px 14px', borderRadius:20, fontSize:'0.78rem', cursor:'pointer', border:`1.5px solid ${statusFilter===s?'var(--amber)':'var(--border)'}`, background:statusFilter===s?'var(--amber-pale)':'white', color:statusFilter===s?'var(--amber)':'var(--text-muted)', fontWeight:statusFilter===s?700:400, fontFamily:'inherit' }}>
                {s}
              </button>
            ))}
          </div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : filtered.length===0 ? <div className="empty-state"><div className="empty-icon">📄</div><h3>No invoices found</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Due</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id}>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{inv.invoice_number}</td>
                      <td style={{ fontSize:'0.78rem' }}>{new Date(inv.invoice_date).toLocaleDateString('en-UG')}</td>
                      <td style={{ fontWeight:600 }}>{inv.customer_name}</td>
                      <td>{(inv.amount||0).toLocaleString()}</td>
                      <td style={{ color:'var(--green-mid)', fontWeight:600 }}>{(inv.amount_paid||0).toLocaleString()}</td>
                      <td style={{ fontWeight:700, color:(inv.balance_due||0)>0?'var(--amber)':'var(--green-mid)' }}>{(inv.balance_due||0).toLocaleString()}</td>
                      <td style={{ fontSize:'0.78rem', color:inv.status==='Overdue'?'var(--red)':undefined }}>{inv.due_date?new Date(inv.due_date).toLocaleDateString('en-UG'):'—'}</td>
                      <td>
                        <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                          <span className={`badge ${STATUS_COLORS[inv.status]||'badge-grey'}`}>{inv.status}</span>
                          {inv.credit_override && <span style={{ fontSize:'0.6rem', color:'var(--red)', fontFamily:"'DM Mono',monospace" }}>Credit override</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5 }}>
                          {canEditData && inv.status !== 'Paid' && (
                            <button className="btn btn-primary btn-sm" onClick={() => { setPaymentModal(inv); setPayForm({ amount: inv.balance_due||'', payment_method:'Bank Transfer', reference:'', notes:'' }) }}>
                              💵 Record Payment
                            </button>
                          )}
                          <button className="btn btn-secondary btn-sm" onClick={() => handlePrintInvoice(inv)}>🖨️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'new' && (
        <div className="card">
          <div className="card-title">New Invoice</div>
          {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

          {/* Customer selector */}
          <div className="form-group" style={{ marginBottom:14 }}>
            <label className="form-label">Customer</label>
            <select className="form-select" value={form.customer_id} onChange={e => handleCustomerSelect(e.target.value)}>
              <option value="">Select existing customer or enter manually below</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.over_credit_limit?' ⚠️ Over Limit':''}
                  {c.outstanding_balance>0?` — UGX ${c.outstanding_balance.toLocaleString()} outstanding`:''}
                </option>
              ))}
            </select>
          </div>

          {/* Credit warning */}
          {creditWarning && (
            <div style={{ background:'var(--red-pale)', border:'2px solid var(--red)', borderRadius:10, padding:16, marginBottom:16 }}>
              <div style={{ fontWeight:700, color:'var(--red)', marginBottom:8 }}>⚠️ Credit Warning</div>
              {creditWarning.map((w,i) => <div key={i} style={{ fontSize:'0.82rem', color:'var(--red)', marginBottom:4 }}>{w}</div>)}
              <div style={{ marginTop:12, padding:12, background:'white', borderRadius:8, border:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom: creditOverride?12:0 }} onClick={() => setCreditOverride(!creditOverride)}>
                  <span style={{ fontSize:'1.1rem' }}>{creditOverride?'✅':'⬜'}</span>
                  <span style={{ fontWeight:700, fontSize:'0.85rem' }}>I acknowledge this credit warning and take responsibility for this sale</span>
                </div>
                {creditOverride && (
                  <div>
                    <label className="form-label">Reason for override (required — minimum 20 characters)</label>
                    <textarea className="form-textarea" style={{ minHeight:60 }} placeholder="Explain why you are proceeding despite the credit warning..." value={creditOverrideReason} onChange={e => setCreditOverrideReason(e.target.value)} />
                    <div style={{ fontSize:'0.72rem', color:creditOverrideReason.length>=20?'var(--green-mid)':'var(--text-muted)', marginTop:4 }}>
                      {creditOverrideReason.length}/20 characters minimum
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Invoice Number *</label><input className="form-input" value={form.invoice_number} onChange={e => setForm(f=>({...f,invoice_number:e.target.value}))} placeholder="INV-0001" /></div>
            <div className="form-group"><label className="form-label">Invoice Date *</label><input className="form-input" type="date" value={form.invoice_date} onChange={e => setForm(f=>({...f,invoice_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Customer Name *</label><input className="form-input" value={form.customer_name} onChange={e => setForm(f=>({...f,customer_name:e.target.value}))} placeholder="Customer name" /></div>
            <div className="form-group"><label className="form-label">Customer Email</label><input className="form-input" type="email" value={form.customer_email} onChange={e => setForm(f=>({...f,customer_email:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Payment Terms</label>
              <select className="form-select" value={form.payment_terms} onChange={e => setForm(f=>({...f,payment_terms:parseInt(e.target.value)}))}>
                {PAYMENT_TERMS.map(d => <option key={d} value={d}>{d===0?'On Delivery':`Net ${d} days`}</option>)}
              </select>
            </div>
          </div>

          <ProduceAvailability />

          <div style={{ fontWeight:700, marginBottom:10 }}>Invoice Items</div>
          <div className="table-wrap" style={{ marginBottom:8 }}>
            <table>
              <thead><tr><th>Description</th><th>Qty (kg)</th><th>Unit Price (UGX)</th><th>Total (UGX)</th><th style={{ width:32 }}></th></tr></thead>
              <tbody>
                {form.items.map((row,i) => (
                  <tr key={i}>
                    <td><input className="form-input" placeholder="e.g. Lettuce Grade A" value={row.description} onChange={e => setItem(i,'description',e.target.value)} /></td>
                    <td><input className="form-input" type="number" step="0.1" style={{ width:80 }} value={row.qty} onChange={e => setItem(i,'qty',e.target.value)} /></td>
                    <td><input className="form-input" type="number" style={{ width:110 }} value={row.unit_price} onChange={e => setItem(i,'unit_price',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width:110, fontWeight:700, background:'#f8f8f0' }} value={row.total?parseInt(row.total).toLocaleString():''} readOnly /></td>
                    <td>{form.items.length>1 && <button className="btn-del-row" onClick={() => setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setForm(f=>({...f,items:[...f.items,emptyItem()]}))}>+ Add Item</button>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', fontWeight:700, color:'var(--green-deep)' }}>Total: UGX {totalAmount.toLocaleString()}</div>
          </div>

          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight:60 }} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Payment terms, delivery details, etc." /></div>
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner" />Saving...</>:'💾 Save Invoice'}</button>
            <button className="btn btn-ghost" onClick={() => setTab('list')}>Cancel</button>
          </div>
        </div>
      )}

      {tab === 'advances' && <AdvancePaymentsTab user={user} canEditData={canEditData} customers={customers} />}

      {/* Payment Modal */}
      {paymentModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setPaymentModal(null)}>
          <div className="modal">
            <div className="modal-title">💵 Record Payment</div>
            <div className="modal-subtitle">
              <strong>{paymentModal.customer_name}</strong> — Invoice {paymentModal.invoice_number}<br/>
              Total: UGX {(paymentModal.amount||0).toLocaleString()} | 
              Paid so far: UGX {(paymentModal.amount_paid||0).toLocaleString()} | 
              <strong style={{ color:'var(--amber)' }}> Balance: UGX {(paymentModal.balance_due||0).toLocaleString()}</strong>
            </div>

            {/* Payment history */}
            {paymentModal.invoice_payments?.length > 0 && (
              <div style={{ background:'var(--cream)', borderRadius:8, padding:12, marginBottom:14 }}>
                <div style={{ fontSize:'0.72rem', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8, color:'var(--text-muted)' }}>Payment History</div>
                {paymentModal.invoice_payments.map((p,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                    <span>{new Date(p.payment_date).toLocaleDateString('en-UG')} — {p.payment_method}</span>
                    <span style={{ fontWeight:700, color:'var(--green-mid)' }}>UGX {(p.amount||0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="form-group"><label className="form-label">Amount Received (UGX) *</label><input className="form-input" type="number" value={payForm.amount} onChange={e => setPayForm(f=>({...f,amount:e.target.value}))} placeholder={paymentModal.balance_due} autoFocus /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Payment Method</label>
                <select className="form-select" value={payForm.payment_method} onChange={e => setPayForm(f=>({...f,payment_method:e.target.value}))}>
                  {['Cash','Bank Transfer','Mobile Money','Cheque','Other'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Reference / Transaction #</label><input className="form-input" value={payForm.reference} onChange={e => setPayForm(f=>({...f,reference:e.target.value}))} placeholder="e.g. TXN-12345" /></div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={payForm.notes} onChange={e => setPayForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes" /></div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setPaymentModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleRecordPayment} disabled={saving||!payForm.amount}>
                {saving?<><span className="spinner" />Saving...</>:'✅ Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

function AdvancePaymentsTab({ user, canEditData, customers }) {
  const [advances, setAdvances] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [showForm, setShowForm] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState(null)
  const [form, setForm] = React.useState({
    customer_id:'', customer_name:'', crop_name:'',
    expected_harvest_date:'', qty_kg:'', price_per_kg:'',
    advance_amount:'', payment_method:'Bank Transfer',
    payment_reference:'', notes:''
  })

  React.useEffect(() => { loadAdvances() }, [])

  const loadAdvances = async () => {
    setLoading(true)
    const { data } = await supabase.from('advance_payments').select('*').order('created_at',{ascending:false})
    setAdvances(data||[])
    setLoading(false)
  }

  const totalOrderValue = (parseFloat(form.qty_kg)||0) * (parseFloat(form.price_per_kg)||0)
  const balanceDue = totalOrderValue - (parseFloat(form.advance_amount)||0)

  const handleSave = async () => {
    if (!form.customer_name || !form.crop_name || !form.advance_amount) {
      setMsg({type:'error',text:'Customer, crop, and advance amount are required.'}); return
    }
    setSaving(true); setMsg(null)
    const { error } = await supabase.from('advance_payments').insert({
      user_id: user.id, customer_id: form.customer_id||null,
      customer_name: form.customer_name, crop_name: form.crop_name,
      expected_harvest_date: form.expected_harvest_date||null,
      qty_kg: parseFloat(form.qty_kg)||null, price_per_kg: parseFloat(form.price_per_kg)||null,
      advance_amount: parseFloat(form.advance_amount),
      total_order_value: totalOrderValue||null,
      balance_due: balanceDue > 0 ? balanceDue : 0,
      payment_method: form.payment_method, payment_reference: form.payment_reference,
      notes: form.notes, status: 'Open'
    })
    if (error) { setMsg({type:'error',text:error.message}); setSaving(false); return }
    setMsg({type:'success',text:'Advance payment recorded.'})
    setSaving(false); setShowForm(false); loadAdvances()
    setTimeout(()=>setMsg(null),3000)
  }

  const statusColor = { Open:'badge-amber', 'Partially Fulfilled':'badge-blue', Fulfilled:'badge-green', Cancelled:'badge-red' }

  return (
    <div>
      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}
      <div className="alert alert-amber" style={{marginBottom:16}}>
        💰 <strong>Advance Payments</strong> — Record when a customer pays in advance for crops not yet harvested. When crop is ready and delivered, create a full invoice and link the advance.
      </div>
      {canEditData && <div style={{marginBottom:12}}><button className="btn btn-primary" onClick={()=>setShowForm(s=>!s)}>+ Record Advance Payment</button></div>}
      {showForm && (
        <div className="card" style={{marginBottom:16,border:'1px solid var(--amber)'}}>
          <div className="card-title">New Advance Payment</div>
          <div className="grid-2" style={{marginBottom:12}}>
            <div className="form-group">
              <label className="form-label">Customer</label>
              <select className="form-select" value={form.customer_id} onChange={e=>{const c=customers.find(c=>c.id===e.target.value);setForm(f=>({...f,customer_id:e.target.value,customer_name:c?.name||''}))}}>
                <option value="">— Select —</option>
                {customers.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!form.customer_id && <input className="form-input" style={{marginTop:6}} placeholder="Or type name" value={form.customer_name} onChange={e=>setForm(f=>({...f,customer_name:e.target.value}))} />}
            </div>
            <div className="form-group"><label className="form-label">Crop</label>
              <select className="form-select" value={form.crop_name} onChange={e=>setForm(f=>({...f,crop_name:e.target.value}))}>
                <option value="">Select</option>{['Lettuce','Strawberries','Mixed','Other'].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Expected Harvest Date</label><input className="form-input" type="date" value={form.expected_harvest_date} onChange={e=>setForm(f=>({...f,expected_harvest_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Qty Ordered (kg)</label><input className="form-input" type="number" value={form.qty_kg} onChange={e=>setForm(f=>({...f,qty_kg:e.target.value}))} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Agreed Price/kg (UGX)</label><input className="form-input" type="number" value={form.price_per_kg} onChange={e=>setForm(f=>({...f,price_per_kg:e.target.value}))} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Total Order Value</label>
              <div style={{padding:'10px 12px',background:'var(--green-pale)',borderRadius:6,fontWeight:700,fontFamily:"'Cormorant Garamond',serif",fontSize:'1.1rem'}}>UGX {totalOrderValue.toLocaleString()}</div>
            </div>
            <div className="form-group"><label className="form-label">Advance Paid (UGX) *</label><input className="form-input" type="number" value={form.advance_amount} onChange={e=>setForm(f=>({...f,advance_amount:e.target.value}))} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Balance Due on Delivery</label>
              <div style={{padding:'10px 12px',background:balanceDue>0?'var(--amber-pale)':'var(--green-pale)',borderRadius:6,fontWeight:700,fontFamily:"'Cormorant Garamond',serif",fontSize:'1.1rem',color:balanceDue>0?'var(--amber)':'var(--green-mid)'}}>UGX {Math.max(0,balanceDue).toLocaleString()}</div>
            </div>
            <div className="form-group"><label className="form-label">Payment Method</label>
              <select className="form-select" value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))}>
                {['Bank Transfer','Cash','Mobile Money','Cheque'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Payment Reference</label><input className="form-input" value={form.payment_reference} onChange={e=>setForm(f=>({...f,payment_reference:e.target.value}))} placeholder="e.g. TXN-12345" /></div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{minHeight:60}} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} /></div>
          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving...':'💾 Save Advance'}</button>
            <button className="btn btn-ghost" onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-title">Advance Payments Log</div>
        {loading?<div style={{textAlign:'center',padding:32}}><span className="spinner dark"/></div>
        :advances.length===0?<div className="empty-state"><div className="empty-icon">💰</div><h3>No advance payments yet</h3></div>
        :(
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Customer</th><th>Crop</th><th>Expected Harvest</th><th>Order Value</th><th>Advance Paid</th><th>Balance Due</th><th>Status</th></tr></thead>
              <tbody>
                {advances.map(a=>(
                  <tr key={a.id}>
                    <td style={{fontSize:'0.78rem'}}>{new Date(a.created_at).toLocaleDateString('en-UG')}</td>
                    <td style={{fontWeight:600}}>{a.customer_name}</td>
                    <td>{a.crop_name}</td>
                    <td style={{fontSize:'0.78rem'}}>{a.expected_harvest_date?new Date(a.expected_harvest_date).toLocaleDateString('en-UG'):'—'}</td>
                    <td style={{fontFamily:"'DM Mono',monospace",fontSize:'0.82rem'}}>{a.total_order_value?parseFloat(a.total_order_value).toLocaleString():'—'}</td>
                    <td style={{fontWeight:700,color:'var(--green-mid)'}}>{parseFloat(a.advance_amount).toLocaleString()}</td>
                    <td style={{fontWeight:700,color:(a.balance_due||0)>0?'var(--amber)':'var(--green-mid)'}}>{(a.balance_due||0).toLocaleString()}</td>
                    <td><span className={`badge ${statusColor[a.status]||'badge-grey'}`}>{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Shows available harvested produce from stores when creating an invoice
function ProduceAvailability() {
  const [produce, setProduce] = React.useState([])
  React.useEffect(() => {
    supabase.from('stock_balances')
      .select('*')
      .eq('category','Harvested Produce')
      .gt('current_balance', 0)
      .then(({ data }) => setProduce(data||[]))
  }, [])

  if (produce.length === 0) return (
    <div className="alert alert-amber" style={{marginBottom:14,fontSize:'0.78rem',padding:'8px 14px'}}>
      ⚠️ No harvested produce currently available in Stores. A sale can only be made for crops that have been harvested and entered stores. Check the Harvest Log and Stores.
    </div>
  )

  return (
    <div style={{background:'var(--green-pale)',border:'1px solid var(--green-light)',borderRadius:8,padding:12,marginBottom:16}}>
      <div style={{fontWeight:700,fontSize:'0.8rem',marginBottom:8,color:'var(--green-deep)'}}>🌾 Available in Stores — can be sold now</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
        {produce.map(p => (
          <span key={p.id} style={{background:'white',border:'1px solid var(--green-light)',borderRadius:6,padding:'4px 12px',fontSize:'0.8rem',fontFamily:"'DM Mono',monospace",color:'var(--green-deep)',fontWeight:600}}>
            {p.name}: {parseFloat(p.current_balance).toFixed(2)} {p.unit}
          </span>
        ))}
      </div>
    </div>
  )
}
